import * as React from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Date picker that emits/consumes an ISO date string (YYYY-MM-DD).
 * Props: value (string), onChange(string), placeholder, testId
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  testId,
  className,
}) {
  const parsed = React.useMemo(() => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  }, [value]);

  const handleSelect = (d) => {
    if (!d) {
      onChange?.("");
      return;
    }
    // store as YYYY-MM-DD
    onChange?.(format(d, "yyyy-MM-dd"));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !parsed && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          <CalendarIcon size={14} />
          {parsed ? format(parsed, "dd MMM yyyy") : placeholder}
          {parsed && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.("");
              }}
              className="ml-auto inline-flex items-center text-muted-foreground hover:text-foreground"
            >
              <X size={14} />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
